import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import './Reports.css';

// Función para traducir estados de pago
const translatePaymentStatus = (status) => {
  const translations = {
    'pending': 'Pendiente',
    'paid': 'Pagado',
    'partial': 'Parcial',
    'overdue': 'Vencido',
    'cancelled': 'Cancelado'
  };
  return translations[status] || status;
};

// Función para traducir estados de asistencia
const translateAttendanceStatus = (status) => {
  const translations = {
    'present': 'Presente',
    'absent': 'Ausente',
    'late': 'Tardío',
    'excused': 'Justificado'
  };
  return translations[status] || status;
};

const Reports = () => {
  const [activeTab, setActiveTab] = useState('payments');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
    groupId: ''
  });
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Obtener grupos para el filtro
  const { data: groups } = useQuery('groups', async () => {
    const response = await api.get('/groups', { params: { is_active: true } });
    return response.data || [];
  });

  // Cargar vista previa cuando cambian los filtros o el tab activo
  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = '';
      let params = {};

      switch (activeTab) {
        case 'payments':
          endpoint = '/payments';
          if (filters.status) params.status = filters.status;
          if (filters.dateFrom) params.date_from = filters.dateFrom;
          if (filters.dateTo) params.date_to = filters.dateTo;
          break;
        case 'students':
          endpoint = '/students';
          params.is_active = true;
          break;
        case 'attendances':
          endpoint = '/attendances';
          if (filters.dateFrom) params.date_from = filters.dateFrom;
          if (filters.dateTo) params.date_to = filters.dateTo;
          if (filters.groupId) params.group_id = filters.groupId;
          break;
        default:
          setPreviewData([]);
          setLoading(false);
          return;
      }

      const response = await api.get(endpoint, { params });
      setPreviewData(response.data || []);
    } catch (error) {
      console.error('Error al cargar vista previa:', error);
      toast.error('Error al cargar vista previa');
      setPreviewData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters]);

  useEffect(() => {
    loadPreview();
    setCurrentPage(1);
  }, [loadPreview]);

  const handleDownload = async (endpoint, filename, params = {}) => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      
      // Agregar filtros a los parámetros
      if (filters.dateFrom) queryParams.append('date_from', filters.dateFrom);
      if (filters.dateTo) queryParams.append('date_to', filters.dateTo);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.groupId) queryParams.append('group_id', filters.groupId);
      
      // Agregar otros parámetros
      Object.keys(params).forEach(key => {
        if (params[key]) queryParams.append(key, params[key]);
      });
      
      const baseUrl = api.defaults.baseURL || 'http://localhost:8000/api/v1';
      const url = `${baseUrl}/reports/${endpoint}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar el reporte');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error al descargar:', error);
      alert('Error al descargar el reporte. Por favor, intente nuevamente.');
    }
  };

  const handleExportPayments = (format) => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    
    const filename = `pagos_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
    handleDownload(`payments/${format}`, filename, params);
  };

  const handleExportFinancial = () => {
    const params = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    
    handleDownload('financial/excel', `reporte_financiero_${new Date().toISOString().split('T')[0]}.xlsx`, params);
  };

  const handleExportStudents = () => {
    handleDownload('students/excel', `estudiantes_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportAttendances = () => {
    const params = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.groupId) params.group_id = filters.groupId;
    
    handleDownload('attendances/excel', `asistencias_${new Date().toISOString().split('T')[0]}.xlsx`, params);
  };

  // Renderizar filtros según el tab activo
  const renderFilters = () => {
    return (
      <div className="card filters-section">
        <h3>Filtros (Opcionales)</h3>
        <div className="filters-grid">
          {(activeTab === 'payments' || activeTab === 'financial' || activeTab === 'attendances') && (
            <>
              <div className="form-group">
                <label>Fecha Desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Fecha Hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
              </div>
            </>
          )}
          
          {activeTab === 'payments' && (
            <div className="form-group">
              <label>Estado de Pago</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="pending">Pendiente</option>
                <option value="paid">Pagado</option>
                <option value="partial">Parcial</option>
                <option value="overdue">Vencido</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
          )}
          
          {activeTab === 'attendances' && (
            <div className="form-group">
              <label>Grupo</label>
              <select
                value={filters.groupId}
                onChange={(e) => setFilters({ ...filters, groupId: e.target.value })}
              >
                <option value="">Todos los grupos</option>
                {groups?.map(group => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Renderizar vista previa según el tab activo
  const renderPreview = () => {
    if (loading) {
      return (
        <div className="preview-loading">
          <p>Cargando vista previa...</p>
        </div>
      );
    }

    if (!previewData || previewData.length === 0) {
      return (
        <div className="preview-empty">
          <p>No hay datos para mostrar con los filtros seleccionados</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'payments':
        return renderPaymentsPreview();
      case 'students':
        return renderStudentsPreview();
      case 'attendances':
        return renderAttendancesPreview();
      case 'financial':
        return renderFinancialPreview();
      default:
        return null;
    }
  };

  const renderPaymentsPreview = () => (
    <div className="preview-table-container">
      <table className="preview-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Estudiante</th>
            <th>Grupo</th>
            <th>Fecha</th>
            <th>Monto</th>
            <th>Pagado</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {previewData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(payment => (
            <tr key={payment.id}>
              <td>{payment.id}</td>
              <td>{payment.student?.full_name || 'Sin estudiante'}</td>
              <td>{payment.enrollment?.group?.name || 'Sin grupo'}</td>
              <td>{payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : ''}</td>
              <td>S/ {payment.amount}</td>
              <td>S/ {payment.paid_amount || 0}</td>
              <td>
                <span className={`status-badge status-${payment.status}`}>
                  {translatePaymentStatus(payment.status)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {previewData.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, previewData.length)} - {Math.min(currentPage * itemsPerPage, previewData.length)} de {previewData.length} registros
          </div>
          <div className="pagination-controls">
            <button className="pagination-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>« Primera</button>
            <button className="pagination-btn" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>‹ Anterior</button>
            <span className="pagination-current">Página {currentPage} de {Math.ceil(previewData.length / itemsPerPage)}</span>
            <button className="pagination-btn" onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(previewData.length / itemsPerPage)))} disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}>Siguiente ›</button>
            <button className="pagination-btn" onClick={() => setCurrentPage(Math.ceil(previewData.length / itemsPerPage))} disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}>Última »</button>
          </div>
        </div>
      )}
    </div>
  );

  const renderStudentsPreview = () => (
    <div className="preview-table-container">
      <table className="preview-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Nombre Completo</th>
            <th>Documento</th>
            <th>Email</th>
            <th>Teléfono</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {previewData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(student => (
            <tr key={student.id}>
              <td>{student.id}</td>
              <td>{student.full_name}</td>
              <td>{student.document_type} {student.document_number}</td>
              <td>{student.email || '-'}</td>
              <td>{student.phone || '-'}</td>
              <td>
                <span className={`status-badge ${student.is_active ? 'status-paid' : 'status-cancelled'}`}>
                  {student.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {previewData.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, previewData.length)} - {Math.min(currentPage * itemsPerPage, previewData.length)} de {previewData.length} registros
          </div>
          <div className="pagination-controls">
            <button className="pagination-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>« Primera</button>
            <button className="pagination-btn" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>‹ Anterior</button>
            <span className="pagination-current">Página {currentPage} de {Math.ceil(previewData.length / itemsPerPage)}</span>
            <button className="pagination-btn" onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(previewData.length / itemsPerPage)))} disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}>Siguiente ›</button>
            <button className="pagination-btn" onClick={() => setCurrentPage(Math.ceil(previewData.length / itemsPerPage))} disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}>Última »</button>
          </div>
        </div>
      )}
    </div>
  );

  const renderAttendancesPreview = () => (
    <div className="preview-table-container">
      <table className="preview-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Estudiante</th>
            <th>Grupo</th>
            <th>Fecha</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {previewData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(attendance => (
            <tr key={attendance.id}>
              <td>{attendance.id}</td>
              <td>{attendance.student?.full_name || 'Sin estudiante'}</td>
              <td>{attendance.group?.name || 'Sin grupo'}</td>
              <td>{attendance.attendance_date ? new Date(attendance.attendance_date).toLocaleDateString() : ''}</td>
              <td>
                <span className={`status-badge status-${attendance.status}`}>
                  {translateAttendanceStatus(attendance.status)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {previewData.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, previewData.length)} - {Math.min(currentPage * itemsPerPage, previewData.length)} de {previewData.length} registros
          </div>
          <div className="pagination-controls">
            <button className="pagination-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>« Primera</button>
            <button className="pagination-btn" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>‹ Anterior</button>
            <span className="pagination-current">Página {currentPage} de {Math.ceil(previewData.length / itemsPerPage)}</span>
            <button className="pagination-btn" onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(previewData.length / itemsPerPage)))} disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}>Siguiente ›</button>
            <button className="pagination-btn" onClick={() => setCurrentPage(Math.ceil(previewData.length / itemsPerPage))} disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}>Última »</button>
          </div>
        </div>
      )}
    </div>
  );

  const renderFinancialPreview = () => (
    <div className="financial-summary">
      <p>El reporte financiero incluye estadísticas y resumen de ingresos.</p>
      <p>Total de pagos en el período: {previewData.length}</p>
    </div>
  );

  return (
    <div className="reports-page">
      <div className="page-header">
        <h2>� Reportes</h2>
        <p>Visualiza y exporta reportes en formato Excel o PDF</p>
      </div>

      {/* Tabs de navegación */}
      <div className="reports-tabs">
        <button
          className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          � Reportes de Pagos
        </button>
        <button
          className={`tab-button ${activeTab === 'financial' ? 'active' : ''}`}
          onClick={() => setActiveTab('financial')}
        >
          📈 Reporte Financiero
        </button>
        <button
          className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          👥 Lista de Estudiantes
        </button>
        <button
          className={`tab-button ${activeTab === 'attendances' ? 'active' : ''}`}
          onClick={() => setActiveTab('attendances')}
        >
          ✅ Reporte de Asistencias
        </button>
      </div>

      {/* Filtros */}
      {renderFilters()}

      {/* Vista previa */}
      <div className="card preview-section">
        <div className="preview-header">
          <h3>Vista Previa</h3>
          <div className="export-buttons">
            {activeTab === 'payments' && (
              <>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleExportPayments('excel')}
                >
                  📊 Exportar a Excel
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleExportPayments('pdf')}
                >
                  📄 Exportar a PDF
                </button>
              </>
            )}
            {activeTab === 'financial' && (
              <button 
                className="btn btn-primary"
                onClick={handleExportFinancial}
              >
                📊 Exportar a Excel
              </button>
            )}
            {activeTab === 'students' && (
              <button 
                className="btn btn-primary"
                onClick={handleExportStudents}
              >
                📊 Exportar a Excel
              </button>
            )}
            {activeTab === 'attendances' && (
              <button 
                className="btn btn-primary"
                onClick={handleExportAttendances}
              >
                📊 Exportar a Excel
              </button>
            )}
          </div>
        </div>
        {renderPreview()}
      </div>
    </div>
  );
};

export default Reports;

